// components/dashboard/ResourceBits.jsx
//
// Small presentational pieces shared by the resource list views (Avatars,
// Agents). Keeping them here is what makes those views look like one system
// instead of two similar-ish screens that drift apart.
import React from "react";
import { CheckCircle2, FileEdit } from "lucide-react";

/**
 * Draft vs published, pinned to the top-right of a card cover.
 * The agent equivalent of the avatar's public/private badge.
 */
export const StatusBadge = ({ isDraft }) => (
  <span
    title={isDraft ? "Draft" : "Live"}
    aria-label={isDraft ? "Draft agent" : "Live agent"}
    className={`absolute right-2.5 top-2.5 inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-semibold backdrop-blur-sm ${
      isDraft
        ? "border-amber-400/30 bg-black/60 text-amber-300"
        : "border-emerald-400/30 bg-black/60 text-emerald-300"
    }`}
  >
    {isDraft ? <FileEdit className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
    {isDraft ? "Draft" : "Live"}
  </span>
);

/** Neutral pill for structural facts (sections, language, model, skills…). */
export const MetaChip = ({ children }) => {
  if (children === null || children === undefined || children === "") return null;
  return (
    <span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-0.5 text-[11px] font-medium text-gray-300">
      {children}
    </span>
  );
};

/** Purple pill for user-defined tags. */
export const TagChip = ({ children }) => (
  <span className="rounded-full border border-purple-500/30 bg-purple-600/15 px-2.5 py-0.5 text-[11px] text-purple-200">
    {children}
  </span>
);

/**
 * Summary tile for the stats strip above a resource grid.
 * `approx` marks a count as partial — more pages are still unloaded, so the
 * number would otherwise read as the real total.
 */
export const StatTile = ({ Icon, label, value, sub, tone = "violet", approx = false }) => (
  <div className="flex items-center gap-2.5 sm:gap-3">
    <span
      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ring-1 sm:h-10 sm:w-10 ${
        tone === "pink"
          ? "bg-pink-500/15 text-pink-300 ring-pink-400/20"
          : "bg-violet-500/15 text-violet-200 ring-violet-400/20"
      }`}
    >
      <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
    </span>
    <div className="min-w-0">
      {/* Se parte en dos líneas en vez de recortarse. Medido a 360/390/430px
          con tres métricas en fila, la etiqueta dispone de 49-73px y
          "TOTAL AGENTS" no cabe en una: con `truncate` se leía "TOTAL AG…",
          que no dice nada. Partida cabe entera y el tile sólo crece 5px. */}
      <p className="text-[10px] uppercase leading-tight tracking-wide text-gray-400 sm:text-[11px]">{label}</p>
      <p className="text-lg font-bold tabular-nums text-white sm:text-xl">
        {value}
        {approx && <span className="text-gray-500">+</span>}
      </p>
      {/* The sub-label is the first thing to go on phones — least load-bearing. */}
      {sub && <p className="hidden truncate text-[11px] text-gray-500 sm:block">{sub}</p>}
    </div>
  </div>
);

/** "Jul 6, 2026" — falls back to an em dash for missing/invalid dates. */
export const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/** Newest timestamp available on a resource, preferring updates over creation. */
export const updatedAtOf = (r) =>
  r?.updatedAt || r?.updated_at || r?.createdAt || r?.created_at || null;
